/**
 * WhiteboardLanguageEditor — define your "visual language" where you use it.
 *
 * The import reads the workspace's shared signifier rules (the same 'items'
 * color map Miro uses), but that config had no home in the UI. This is a
 * focused editor, launched from the whiteboard flow, that lets a user map a
 * visual cue (fill / border / text color, shape, or connector line style) to an
 * item attribute (category, altitude, a numeric attribute like impact) or — for
 * line style — a relationship type. Saves back to the shared map so it applies
 * to every capture, and to Miro.
 */

import { useCallback, useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, Check, Sparkles } from 'lucide-react';
import {
  loadMiroColorMaps,
  saveMiroColorMaps,
  getColorRulesForEntity,
  ruleDimension,
  isColorSignifierDimension,
  CONNECTOR_SIGNIFIER_DIMENSIONS,
  type MiroColorRule,
  type SignifierDimension,
  type MiroEntityColorMap,
} from '../../../lib/fieldMappingRegistry';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { describeSignifierRule } from '../../../lib/whiteboardSignifiers';

const DIMENSIONS: Array<{ value: SignifierDimension; label: string }> = [
  { value: 'shape_color', label: 'Shape color' },
  { value: 'fill_color', label: 'Shape color (legacy fill)' },
  { value: 'shape', label: 'Shape' },
  { value: 'line_color', label: 'Line color' },
  { value: 'line_type', label: 'Line type' },
  { value: 'arrow_color', label: 'Arrow color' },
  { value: 'arrow_shape', label: 'Arrow shape' },
  { value: 'border_color', label: 'Border color' },
  { value: 'text_color', label: 'Text color' },
  { value: 'line_style', label: 'Line style (legacy)' },
];

const COLOR_NAMES = [
  'yellow', 'orange', 'red', 'pink', 'green', 'light_green', 'blue', 'light_blue',
  'cyan', 'purple', 'violet', 'gray', 'white', 'black',
];
const SHAPE_TOKENS = ['sticky', 'box', 'rounded_box', 'circle', 'diamond', 'cloud', 'text'];
const LINE_TYPE_TOKENS = ['solid', 'dashed'];
const ARROW_SHAPE_TOKENS = ['none', 'single', 'double'];
const LINE_STYLE_TOKENS = ['arrow', 'double_arrow', 'dashed_arrow', 'line', 'dashed_line'];
const REL_TYPES = ['dependency', 'prerequisite', 'related', 'contains', 'breakdown', 'elaboration', 'aggregation'];

const TARGET_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'itemCategory', label: 'Category' },
  { value: 'altitudeLevel', label: 'Altitude (1–5)' },
  { value: 'attribute:impact', label: 'Impact' },
  { value: 'attribute:value', label: 'Value' },
  { value: 'attribute:effort', label: 'Effort' },
  { value: 'attribute:risk', label: 'Risk' },
  { value: 'attribute:importance', label: 'Importance' },
  { value: 'attribute:confidence', label: 'Confidence' },
];

function tokensFor(dim: SignifierDimension): string[] {
  if (isColorSignifierDimension(dim)) return COLOR_NAMES;
  if (dim === 'shape') return SHAPE_TOKENS;
  if (dim === 'line_type') return LINE_TYPE_TOKENS;
  if (dim === 'arrow_shape') return ARROW_SHAPE_TOKENS;
  if (dim === 'line_style') return LINE_STYLE_TOKENS;
  return COLOR_NAMES;
}

const isConnectorDim = (d: SignifierDimension) => CONNECTOR_SIGNIFIER_DIMENSIONS.has(d);

interface Props {
  onClose: () => void;
  onSaved: (rules: MiroColorRule[]) => void;
}

const sel = 'text-xs border border-[var(--rule)] rounded px-1.5 py-1 bg-[var(--bg-raised)] text-[var(--text-sec)] focus:outline-none focus:ring-1 focus:ring-primary-400';

export function WhiteboardLanguageEditor({ onClose, onSaved }: Props) {
  const ws = getCurrentWorkspaceId();
  const [rules, setRules] = useState<MiroColorRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ws) { setLoading(false); return; }
    let active = true;
    loadMiroColorMaps(ws)
      .then((maps) => { if (active) setRules(getColorRulesForEntity(maps, 'items')); })
      .catch(() => { /* none configured yet */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ws]);

  const update = (i: number, patch: Partial<MiroColorRule>) =>
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRule = () =>
    setRules((rs) => [...rs, { color: '', dimension: 'shape_color', field: 'itemCategory', value: '' }]);
  const removeRule = (i: number) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const save = useCallback(async () => {
    const clean = rules.filter((r) => {
      if (!r.color || !r.field) return false;
      if (isConnectorDim(ruleDimension(r))) return Boolean(r.value);
      return true;
    });
    if (!ws) { onSaved(clean); onClose(); return; }
    setSaving(true);
    try {
      const maps = await loadMiroColorMaps(ws);
      const itemsId = maps.find((m) => m.entityType === 'items')?.id ?? crypto.randomUUID();
      const next: MiroEntityColorMap[] = [
        ...maps.filter((m) => m.entityType !== 'items'),
        { id: itemsId, entityType: 'items', rules: clean },
      ];
      await saveMiroColorMaps(ws, next);
      onSaved(clean);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [ws, rules, onSaved, onClose]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)]">
          <span className="flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)]">
            <Sparkles className="h-4 w-4 text-primary-600" /> Visual language
          </span>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          <p className="text-sm text-[var(--text-ter)]">
            Map a visual cue to an attribute, so a marker color or shape carries data into the platform automatically.
            Applies to whiteboard photos, projection, and Miro.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {rules.length === 0 && (
                <p className="text-sm text-[var(--text-ter)] italic py-2">No rules yet. Add one below — e.g. green fill → impact 10.</p>
              )}
              <div className="space-y-2">
                {rules.map((rule, i) => {
                  const dim = ruleDimension(rule);
                  return (
                    <div key={i} className="flex items-center gap-2 flex-wrap p-2 border border-[var(--rule)] bg-[var(--surface)]/60">
                      <select value={dim} onChange={(e) => {
                        const d = e.target.value as SignifierDimension;
                        update(i, {
                          dimension: d,
                          color: '',
                          field: isConnectorDim(d) ? 'relationshipType' : 'itemCategory',
                          value: '',
                        });
                      }} className={sel}>
                        {DIMENSIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>

                      <select value={rule.color} onChange={(e) => update(i, { color: e.target.value })} className={sel}>
                        <option value="">— token —</option>
                        {tokensFor(dim).map((t) => (
                          <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                        ))}
                      </select>

                      <span className="text-xs text-[var(--text-ter)]">→</span>

                      {isConnectorDim(dim) ? (
                        <select value={rule.value ?? ''} onChange={(e) => update(i, { field: 'relationshipType', value: e.target.value })} className={sel}>
                          <option value="">— relationship —</option>
                          {REL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : (
                        <>
                          <select value={rule.field} onChange={(e) => update(i, { field: e.target.value })} className={sel}>
                            {TARGET_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                          <span className="text-xs text-[var(--text-ter)]">=</span>
                          <input
                            value={rule.value ?? ''}
                            onChange={(e) => update(i, { value: e.target.value })}
                            placeholder={rule.field === 'itemCategory' ? 'risk' : '10'}
                            className="w-20 text-xs border border-[var(--rule)] rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        </>
                      )}

                      <button onClick={() => removeRule(i)} className="ml-auto p-1 text-[var(--text-ter)] hover:text-[var(--coral)]" title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <button onClick={addRule} className="flex items-center gap-1 text-sm text-primary-600 hover:underline">
                <Plus className="h-4 w-4" /> Add rule
              </button>

              {rules.some((r) => r.color && r.field) && (
                <div className="bg-primary-50/50 border border-primary-100 p-2.5">
                  <p className="text-[11px] uppercase tracking-wide text-primary-700 font-semibold mb-1">Preview</p>
                  <div className="flex flex-wrap gap-1.5">
                    {rules.filter((r) => r.color && r.field).map((r, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[var(--bg-raised)] border border-primary-200 text-[11px] text-[var(--text-sec)]">
                        {describeSignifierRule(r)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[var(--rule)] flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save language
          </button>
        </div>
      </div>
    </div>
  );
}

export default WhiteboardLanguageEditor;
